/**
 * This hook is used to fetch drivers from the server based on the search string.
 */

import React, { useEffect, useState, useRef } from 'react';
import debounce from 'lodash.debounce';

import { TwAutocompleteOption } from '../components/TwAutocomplete/TwAutocomplete';
import { useLazySearchDriversQuery } from '../redux/api/driver/driverApi';
import { Driver } from '../redux/api/driver/driver.model';
import useNotifications from '../providers/Notifications/Notifications.provider';

const useDriverSearch = (): [
  TwAutocompleteOption[],
  React.Dispatch<React.SetStateAction<TwAutocompleteOption[]>>,
  (search: string | undefined) => void
] => {
  const [driverLabels, setDriverLabels] = useState<TwAutocompleteOption[]>([]);
  const [searchDriver] = useLazySearchDriversQuery();
  const { addNotification } = useNotifications();

  const debouncedSearch = useRef(
    // wait for 300 millisecond after last keystroke before api call
    debounce((search: string) => {
      searchDriver({
        params: {
          search
        }
      })
        .then((response) => {
          if (!response) return;
          const labels: TwAutocompleteOption[] = [];
          response?.data?.content?.map((driver: Driver) => {
            labels.push({
              id: driver.id,
              label: driver.name,
              value: driver.name
            });
          });
          setDriverLabels(labels);
        })
        .catch((error) => {
          console.error('Error while fetching drivers', error);
          addNotification({
            message: 'Error while fetching drivers',
            variant: 'error'
          });
        });
    }, 300)
  ).current;

  useEffect(() => {
    return (): void => {
      debouncedSearch.cancel();
    };
  }, [debouncedSearch]);

  const fetchDrivers = (search?: string): void => {
    if (!search) {
      debouncedSearch.cancel();
      setDriverLabels([]);
      return;
    }
    debouncedSearch(search);
  };

  return [driverLabels, setDriverLabels, fetchDrivers];
};

export default useDriverSearch;
